import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import "./Unturned.css";
import "./public.css";
import 'bootstrap/dist/css/bootstrap.css';
import Flag from 'react-world-flags';


const latencyProbeByLocation = {
  "us east": "https://sky.sodiumhosting.com:8080",
  "germany": "https://aston-game-ger.sodiumhosting.com:8080",
  "frankfurt": "https://aston-game-ger.sodiumhosting.com:8080",
};

const getProbeUrlFromLocationName = (name = "") => latencyProbeByLocation[name.trim().toLowerCase()] || null;

const measureLatency = (url, timeoutMs = 1200) => new Promise((resolve) => {
  if (!url) {
    resolve(null);
    return;
  }

  const img = new Image();
  const started = performance.now();
  const timer = setTimeout(() => {
    img.src = "";
    resolve(null);
  }, timeoutMs);

  const finish = () => {
    clearTimeout(timer);
    resolve(Math.round(performance.now() - started));
  };

  img.onload = finish;
  img.onerror = finish;
  img.src = `${url}/favicon.ico?ts=${Date.now()}-${Math.random()}`;
});

export const Unturned = () => {
  const [packageDetails, setPackageDetails] = useState([]);
  const [affiliateID, setAffiliateID] = useState(null);
  const [locationOption, setlocationOption] = useState([]);
  const [locations, setlocations] = useState([]);
  const [activeLocation, setActiveLocation] = useState(null);
  const [latencyByLocation, setLatencyByLocation] = useState({});
  const [isPinging, setIsPinging] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/unturned`;
  const description = "High-performance Unturned hosting with quick setup, low-latency regions, and flexible scaling for survival communities.";
  useEffect(() => {
    fetch('/unturned.json')
      .then(res => res.json())
      .then(data => {
        setPackageDetails(data.packages);
        setlocationOption(data.locationOption);
        setlocations(data.locations);

        if (data.locations && data.locations.length > 0) {
          setActiveLocation(data.locations[0].id);
        }
      })
      .catch(error => console.error(error));

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const affiliateIDFromCookie = getCookie("partnerAffiliateID");
    if (affiliateIDFromCookie) {
      setAffiliateID(affiliateIDFromCookie);
    }
  }, []);
  const handleLocationSelect = (locationId) => {
    setActiveLocation(locationId);
  };

  const runLatencyCheck = useCallback(async () => {
    if (locations.length === 0) {
      return;
    }

    setIsPinging(true);
    const nextLatencies = {};

    await Promise.all(locations.map(async (location) => {
      const probeUrl = getProbeUrlFromLocationName(location.name);
      const measured = await measureLatency(probeUrl);
      nextLatencies[location.id] = typeof measured === "number" && measured < 1200 ? measured : null;
    }));

    setLatencyByLocation(nextLatencies);
    setIsPinging(false);
  }, [locations]);

  useEffect(() => {
    runLatencyCheck();
  }, [runLatencyCheck]);

  const sortedLocations = useMemo(() => {
    return [...locations].sort((a, b) => {
      const aLatency = latencyByLocation[a.id];
      const bLatency = latencyByLocation[b.id];

      if (typeof aLatency !== "number" && typeof bLatency !== "number") {
        return 0;
      }
      if (typeof aLatency !== "number") {
        return 1;
      }
      if (typeof bLatency !== "number") {
        return -1;
      }
      return aLatency - bLatency;
    });
  }, [latencyByLocation, locations]);


  return (
    <div className="game-page unturned-page">
      <Helmet>
        <title>FlintHost | Unturned Hosting</title>
        <meta name="description" content={description} />
        <meta property="og:title" content="FlintHost | Unturned Hosting" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${origin}/img/Logo2.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FlintHost | Unturned Hosting" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${origin}/img/Logo2.png`} />
      </Helmet>
      <section className="game-hero">
        <div className="game-hero-content">
          <p className="game-eyebrow">Unturned Hosting</p>
          <h1>Fast load times for survival worlds.</h1>
          <p className="game-lead">
            Spin up a new Unturned server in seconds with next-gen hardware and low-latency routing.
          </p>
          <div className="game-pill-row">
            <span>Low latency</span>
            <span>Live backups</span>
            <span>Secure networking</span>
          </div>
        </div>
        <div className="location-actions">
          <button type="button" className="primary-link reping-button" onClick={runLatencyCheck} disabled={isPinging}>
            {isPinging ? "Re-pinging..." : "Re-ping locations"}
          </button>
        </div>
      </section>

      <section className="game-section">
        <div className="section-heading">
          <h2 className="section-title">Server location</h2>
          <p className="section-subtitle">Choose the closest region for faster connections.</p>
        </div>
        <div className="location-grid">
          {sortedLocations.map(location => (
            <button
              key={location.id}
              className={`locationbox ${activeLocation === location.id ? 'is-active' : ''}`}
              onClick={() => handleLocationSelect(location.id)}
            >
              <span className="location-title">
                <Flag code={location.countryCode} style={{ width: "20px", marginRight: "10px" }} />
                {location.name}
              </span>
              <span className="location-action">
                {activeLocation === location.id ? 'Selected' : 'Select'}
              </span>
              <span className="location-ping">
                {typeof latencyByLocation[location.id] === "number" ? `~${latencyByLocation[location.id]} ms` : "Ping unavailable"}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="game-section">
        <div className="section-heading">
          <h2 className="section-title">Unturned Server Packages</h2>
          <p className="section-subtitle">Balanced performance tiers for every play style.</p>
        </div>
        <div className="game-cont">
          <div className="pricing-grid">
            {packageDetails.map(packageDetail => (
              <div key={packageDetail.id} className={`packagebox ${packageDetail.id}`}>
                <h3>{packageDetail.name}</h3>
                <p>{packageDetail.slots} Player Slots</p>
                <p>{packageDetail.memory/1024} GB RAM</p>
                <p>{packageDetail.cpu/100} {packageDetail.cpu/100 === 1 ? 'vCore' : 'vCores'}</p>
                <p>Pterodactyl Panel</p>
                <a
                  className="primary-link"
                  href={`${packageDetail.link}?options[${locationOption}]=${activeLocation}${affiliateID ? `&ref=${encodeURIComponent(affiliateID)}` : ""}`}
                >
                  Order Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
